// Blackboard — EM position definitions and training plans
// Ported 1:1 from positions.js (Firebase Blackboard app)

import {
  IconShield,
  IconMap,
  IconTruck,
  IconCoin,
  IconSpeakerphone,
  IconFlame,
  IconHeartHandshake,
  IconCamera,
  IconAlertTriangle,
  IconLink,
  IconAntenna,
  IconUmbrella,
} from '@tabler/icons-react';
import type { FC } from 'react';

export interface EMPosition {
  value: string;
  label: string;
  icon: FC<{ size?: number | string; color?: string; [key: string]: any }>;
  color: string;
}

export const EM_POSITIONS: EMPosition[] = [
  { value: 'operations', label: 'Operations', icon: IconShield, color: '#e53e3e' },
  { value: 'planning', label: 'Planning', icon: IconMap, color: '#3182ce' },
  { value: 'logistics', label: 'Logistics', icon: IconTruck, color: '#38a169' },
  { value: 'finance_admin', label: 'Finance/Admin', icon: IconCoin, color: '#d69e2e' },
  { value: 'external_affairs', label: 'External Affairs', icon: IconSpeakerphone, color: '#805ad5' },
  { value: 'response', label: 'Response', icon: IconFlame, color: '#dd6b20' },
  { value: 'recovery', label: 'Recovery', icon: IconHeartHandshake, color: '#2b6cb0' },
  { value: 'pio', label: 'Public Information Officer', icon: IconCamera, color: '#00b5d8' },
  { value: 'safety_officer', label: 'Safety Officer', icon: IconAlertTriangle, color: '#c53030' },
  { value: 'liaison', label: 'Liaison Officer', icon: IconLink, color: '#2f855a' },
  { value: 'communications', label: 'Communications', icon: IconAntenna, color: '#6b46c1' },
  { value: 'mitigation', label: 'Mitigation', icon: IconUmbrella, color: '#319795' },
];

export const POSITION_OPTIONS = EM_POSITIONS.map(p => ({ value: p.value, label: p.label }));

export const POSITION_MAP: Record<string, EMPosition> = Object.fromEntries(
  EM_POSITIONS.map(p => [p.value, p]),
);

export const CORE_TRAININGS = ['IS-100', 'IS-200', 'IS-700', 'IS-800'];

export const DEFAULT_POSITION_PLANS: Record<string, { label: string; trainings: string[] }> = {
  operations: { label: 'Operations', trainings: [...CORE_TRAININGS, 'IS-300', 'IS-400', 'ICS-300', 'ICS-400', 'HAZWOPER 40-Hour', 'CPR/First Aid', 'Bloodborne Pathogens', 'Active Shooter/Threat Training', 'Basic Emergency Operations Center', 'Stop the Bleed'] },
  planning: { label: 'Planning', trainings: [...CORE_TRAININGS, 'IS-300', 'IS-235', 'IS-230', 'IS-240', 'IS-241', 'IS-242', 'Basic Emergency Operations Center'] },
  logistics: { label: 'Logistics', trainings: [...CORE_TRAININGS, 'IS-300', 'IS-230', 'IS-242', 'HAZWOPER 24-Hour', 'CPR/First Aid', 'Basic Emergency Operations Center', 'Emergency Communications/COML'] },
  finance_admin: { label: 'Finance/Admin', trainings: [...CORE_TRAININGS, 'IS-230', 'IS-241', 'IS-242', 'Basic Emergency Operations Center'] },
  external_affairs: { label: 'External Affairs', trainings: [...CORE_TRAININGS, 'IS-230', 'IS-242', 'IS-240', 'IS-244', 'Psychological First Aid'] },
  response: { label: 'Response', trainings: [...CORE_TRAININGS, 'IS-300', 'IS-400', 'ICS-300', 'ICS-400', 'HAZWOPER 40-Hour', 'CPR/First Aid', 'Stop the Bleed', 'Bloodborne Pathogens', 'Active Shooter/Threat Training', 'CERT Basic Training', 'NWS Skywarn Spotter Training', 'Basic Emergency Operations Center'] },
  recovery: { label: 'Recovery', trainings: [...CORE_TRAININGS, 'IS-230', 'IS-235', 'IS-241', 'IS-242', 'IS-244', 'Psychological First Aid', 'HAZWOPER 24-Hour'] },
  pio: { label: 'Public Information Officer', trainings: [...CORE_TRAININGS, 'IS-300', 'IS-242', 'IS-240', 'IS-230', 'Basic Emergency Operations Center', 'Active Shooter/Threat Training'] },
  safety_officer: { label: 'Safety Officer', trainings: [...CORE_TRAININGS, 'IS-300', 'HAZWOPER 40-Hour', 'CPR/First Aid', 'Stop the Bleed', 'Bloodborne Pathogens', 'Active Shooter/Threat Training', 'Psychological First Aid', 'CERT Basic Training'] },
  liaison: { label: 'Liaison Officer', trainings: [...CORE_TRAININGS, 'IS-300', 'IS-230', 'IS-240', 'IS-242', 'IS-244', 'Basic Emergency Operations Center'] },
  communications: { label: 'Communications', trainings: [...CORE_TRAININGS, 'IS-300', 'Emergency Communications/COML', 'ARES/RACES Communications', 'Basic Emergency Operations Center', 'NWS Skywarn Spotter Training'] },
  mitigation: { label: 'Mitigation', trainings: [...CORE_TRAININGS, 'IS-230', 'IS-235', 'IS-241', 'IS-242', 'HAZWOPER 24-Hour', 'NWS Skywarn Spotter Training'] },
};

export function getTrainingsForPosition(
  positionValue: string,
  allTrainings: { name: string }[],
): { name: string }[] {
  const plan = DEFAULT_POSITION_PLANS[positionValue];
  if (!plan) return [];
  return allTrainings.filter(t => plan.trainings.some(keyword => t.name.includes(keyword)));
}
